import type {ErrorEvent} from '@sentry/core';
import {type BrowserOptions, type Breadcrumb, type BreadcrumbHint, type EventHint} from '@sentry/nuxt';
import {options as commonSentryOptions} from '@malt/alerting-shared';
import {MicroFrontendBuildAssetsDirMapper} from '@malt/nuxt-micro-frontend-types';
import {simplifyUiInteractionBreadcrumb} from './sentry.breadcrumb.utils';

const {...commonOptions} = commonSentryOptions;
// Datadog agent calls can fill all SSR breadcrumbs and hide useful request context.
// Example: https://malt.sentry.io/issues/7518964229/events/143d1b94adca444f8466d5e3350d62d9/
const localTelemetryPaths = ['/v0.7/config', '/dogstatsd/', '/profiling/', '/telemetry/'];
// Stale client bundles can request hashes no longer served by the current SSR pod.
// Example: https://malt.sentry.io/issues/7539627709/
const nuxtAssetPathPattern = /^\/_nuxt\/.+\.(?:css|ico|jpe?g|js|json|map|png|svg|webp|woff2?)$/i;
// Micro-frontends serve hashed bundles under /<app>/_nuxt/ (e.g. /profile/_nuxt/CkDnHvx6.js).
// Example: https://malt.sentry.io/issues/MALT-3MME/
const nuxtAssetPathnamePattern = /\/_nuxt\//;
// GTM/GA/Ads collect calls can fill the breadcrumb cap and hide useful app context.
// Example: https://malt.sentry.io/issues/MALT-4P1P/
const thirdPartyTrackingHostPatterns = [
    /(^|\.)googletagmanager\.com$/i,
    /(^|\.)google-analytics\.com$/i,
    /(^|\.)analytics\.google\.com$/i,
    /(^|\.)doubleclick\.net$/i,
    /(^|\.)googleadservices\.com$/i,
    /(^|\.)googlesyndication\.com$/i,
];
const googleTrackingPathPatterns = [/^\/ccm\//, /^\/pagead\//, /^\/ads\//];
// i18n locale bundles are fetched on every MF mount.
const localeAssetPathPattern = /\/locales\/locales\.[a-z]{2}\.json(?:\?|$)/i;
const maltTrackingEventPath = '/api/t/event';
// MF tab switches reload the host HTML shell (e.g. /fees-mf-admin/).
const microFrontendShellPathPattern = /^\/[^/]*-mf-[^/]*\/?$/;
const maltDomainPattern = /(^|\.)malt\.[^.]+$/i;

function getBreadcrumbRequestUrl(breadcrumb: {type?: string; category?: string; data?: Record<string, unknown>}): string | undefined {
    if (typeof breadcrumb.data?.url !== 'string') {
        return undefined;
    }

    const isNetworkBreadcrumb = breadcrumb.type === 'http' || breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr';

    return isNetworkBreadcrumb ? breadcrumb.data.url : undefined;
}

function isThirdPartyTrackingNetworkUrl(url: string): boolean {
    try {
        const {hostname, pathname} = new URL(url, 'https://www.malt.fr');

        if (thirdPartyTrackingHostPatterns.some((pattern) => pattern.test(hostname))) {
            return true;
        }

        return /^(www\.)?google\.com$/i.test(hostname) && googleTrackingPathPatterns.some((pattern) => pattern.test(pathname));
    } catch {
        return false;
    }
}

function isNoisyNetworkBreadcrumbUrl(url: string): boolean {
    if (nuxtAssetPathnamePattern.test(url) || isThirdPartyTrackingNetworkUrl(url) || localeAssetPathPattern.test(url)) {
        return true;
    }

    try {
        const {hostname, pathname} = new URL(url, 'https://www.malt.fr');

        if (pathname === maltTrackingEventPath) {
            return true;
        }

        if (maltDomainPattern.test(hostname) && microFrontendShellPathPattern.test(pathname)) {
            return true;
        }

        return (hostname === 'localhost' || hostname === '127.0.0.1') && localTelemetryPaths.some((path) => pathname.startsWith(path));
    } catch {
        return url === maltTrackingEventPath;
    }
}

/**
 * Options common to Client and Server Sentry configurations
 */
export const sentryOptions = {
    ...commonOptions,
    ignoreErrors: [
        ...commonOptions.ignoreErrors,
        /** Avoid DEV_INTEGRATION mode spam in development*/
        /DEV_INTEGRATION mode enabled/,

        /** Avoid Datadog tracer integrations loading spam */
        /DATADOG TRACER INTEGRATIONS LOADED/,

        /** Avoid NPS feedbacks 404 errors */
        /\[GET] "\/account\/api\/nps\/feedbacks\/user\/active": 404/,

        /** Avoid spam from unlogged and invalid identity users (MALT-4707) */
        /Invalid identity/i,

        /** Avoid expected auth failures from ofetch / universal fetch (any HTTP method) */
        /\[(?:GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\].*: 40[13](?:\s|$)/,

        /** Avoid signin-method-restrictions 503 noise when registration API is unavailable (integration) */
        /\[GET] "[^"]*\/registration\/api\/signin-method-restrictions[^"]*": 503(?:\s|$)/,

        /** Avoid Prismic redirect not found (MALT-4QQM) */
        /Prismic redirect not found for/,

        /** Avoid profile alias resolution errors (404 or 410) — expected when a profile URL / alias doesn't exist */
        /\[GET] "[^"]*\/profile\/public-api\/[^"]+\/url\??[^"]*": (?:404|410)/,
    ],
    tracesSampleRate: 0.001,
    normalizeDepth: 10,
    beforeBreadcrumb: (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => {
        const simplifiedBreadcrumb = simplifyUiInteractionBreadcrumb(breadcrumb, hint);

        // Server-side Sentry records every outgoing HTTP call as a breadcrumb.
        // The Datadog agent runs on localhost and can evict the useful app breadcrumbs from Sentry's 100-breadcrumb limit.
        // Client-side browser tracing records every chunk load under /_nuxt/, which has the same effect.
        const url = getBreadcrumbRequestUrl(simplifiedBreadcrumb);
        if (url && isNoisyNetworkBreadcrumbUrl(url)) {
            return null;
        }
        return simplifiedBreadcrumb;
    },
    beforeSend: (event, hint) => {
        if (isExpectedAuthError(event, hint)) {
            return null;
        }

        // When a browser keeps an old HTML document open across deployments, it may request a hashed Nuxt asset
        // that no longer exists on the current SSR pod. Nitro reports that as a server 404, but it is cache noise.
        const errorContext = event.contexts?.Error;
        const nuxtContext = event.contexts?.nuxt;
        const statusCode = errorContext?.statusCode;
        const nuxtPath = nuxtContext?.path;
        let requestPath: string | undefined;

        if (event.request?.url) {
            try {
                requestPath = new URL(event.request.url).pathname;
            } catch {
                requestPath = undefined;
            }
        }

        const path = typeof nuxtPath === 'string' ? nuxtPath : requestPath;
        if (statusCode === 404 && path && nuxtAssetPathPattern.test(path)) {
            return null;
        }

        const microFrontendApplication = getMicroFrontendApplication(event);
        if (microFrontendApplication) {
            event.tags = {...event.tags, application: microFrontendApplication};
            event.dist = microFrontendApplication;
        }

        const applicationTag = event.tags?.application;
        if (!event.dist && typeof applicationTag === 'string') {
            // Sourcemaps are uploaded with release.dist=<application> to avoid collisions between apps sharing the same Sentry project/release.
            event.dist = applicationTag;
        }

        return commonOptions.beforeSend(event);
    },
} satisfies BrowserOptions;

function getMicroFrontendApplication(event: ErrorEvent): string | undefined {
    const frames = event.exception?.values?.at(-1)?.stacktrace?.frames ?? [];
    const deepestAppFrameFilename = frames.toReversed().find((frame) => frame.filename && nuxtAssetPathnamePattern.test(frame.filename))?.filename;

    if (!deepestAppFrameFilename) {
        return undefined;
    }

    return Object.entries(MicroFrontendBuildAssetsDirMapper).find(([, buildAssetsDir]) =>
        deepestAppFrameFilename.includes(`${buildAssetsDir}/`),
    )?.[0];
}

/**
 * Drop expected auth failures: ofetch FetchError (401/403) and Nuxt/H3 route errors
 * such as `createError({ statusCode: 403, statusMessage: 'Invalid identity' })`.
 */
function isExpectedAuthError(event: {contexts?: Record<string, {statusCode?: number} | undefined>}, hint?: EventHint) {
    const statusCode = getErrorStatusCode(hint?.originalException) ?? event.contexts?.Error?.statusCode;

    return statusCode === 401 || statusCode === 403;
}

function getErrorStatusCode(error: unknown): number | undefined {
    if (!error || typeof error !== 'object') {
        return undefined;
    }

    if ('statusCode' in error && typeof error.statusCode === 'number') {
        return error.statusCode;
    }

    if ('status' in error && typeof error.status === 'number') {
        return error.status;
    }

    return undefined;
}
